import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { Add, Edit, Delete, CallSplit } from "@mui/icons-material";
import {
    orderChannelService,
    OrderChannel,
} from "../../services/orderChannelService";

export const OrderChannelManagement: React.FC = () => {
    const [orderChannels, setOrderChannels] = useState<OrderChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingChannel, setEditingChannel] = useState<OrderChannel | null>(
        null,
    );
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        type: "OFFLINE" as "OFFLINE" | "ONLINE",
        is_active: true,
    });

    useEffect(() => {
        loadOrderChannels();
    }, []);

    const loadOrderChannels = async () => {
        try {
            setLoading(true);
            const channels = await orderChannelService.getOrderChannels(false); // Get all including inactive
            setOrderChannels(channels);
        } catch (err) {
            setError("Failed to load order channels");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (channel?: OrderChannel) => {
        if (channel) {
            setEditingChannel(channel);
            setFormData({
                name: channel.name,
                type: channel.type,
                is_active: channel.is_active,
            });
        } else {
            setEditingChannel(null);
            setFormData({
                name: "",
                type: "OFFLINE",
                is_active: true,
            });
        }
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setEditingChannel(null);
        setError("");
    };

    const handleFormSubmit = async () => {
        try {
            setFormLoading(true);
            setError("");

            if (!formData.name.trim()) {
                setError("Name is required");
                return;
            }

            if (editingChannel) {
                await orderChannelService.updateOrderChannel(
                    editingChannel.order_channel_id,
                    formData,
                );
            } else {
                await orderChannelService.createOrderChannel(formData);
            }

            await loadOrderChannels();
            handleCloseForm();
        } catch (err: any) {
            setError(
                err.response?.data?.detail || "Failed to save order channel",
            );
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (channel: OrderChannel) => {
        if (
            window.confirm(`Are you sure you want to delete "${channel.name}"?`)
        ) {
            try {
                await orderChannelService.deleteOrderChannel(
                    channel.order_channel_id,
                );
                await loadOrderChannels();
            } catch (err: any) {
                setError(
                    err.response?.data?.detail ||
                        "Failed to delete order channel",
                );
            }
        }
    };

    const getTypeColor = (type: string) => {
        return type === "OFFLINE" ? "primary" : "secondary";
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography variant="h4" component="h1">
                    Order Channel Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenForm()}
                >
                    Add Order Channel
                </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Where an order came from — e.g. POS (offline) or a Website
                Form, Messenger, Phone Call (all online). "POS" and "Website
                Form" are used automatically by the POS screen and the
                Online Requests approval flow; add more channels here for
                staff to pick when creating an order manually.
            </Typography>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError("")}
                >
                    {error}
                </Alert>
            )}

            <Box display="flex" flexWrap="wrap" gap={3}>
                {orderChannels.map((channel) => (
                    <Box
                        key={channel.order_channel_id}
                        sx={{
                            flex: {
                                xs: "1 1 100%",
                                md: "1 1 calc(50% - 24px)",
                                lg: "1 1 calc(33.333% - 32px)",
                            },
                            minWidth: 0,
                        }}
                    >
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <CallSplit sx={{ mr: 1 }} />
                                    <Typography variant="h6" component="h2">
                                        {channel.name}
                                    </Typography>
                                </Box>

                                <Box mb={1}>
                                    <Chip
                                        label={channel.type}
                                        color={getTypeColor(channel.type)}
                                        size="small"
                                    />
                                    <Chip
                                        label={
                                            channel.is_active
                                                ? "Active"
                                                : "Inactive"
                                        }
                                        color={
                                            channel.is_active
                                                ? "success"
                                                : "default"
                                        }
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Created:{" "}
                                    {new Date(
                                        channel.created_at,
                                    ).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    startIcon={<Edit />}
                                    onClick={() => handleOpenForm(channel)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<Delete />}
                                    color="error"
                                    onClick={() => handleDelete(channel)}
                                >
                                    Delete
                                </Button>
                            </CardActions>
                        </Card>
                    </Box>
                ))}
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog
                open={formOpen}
                onClose={handleCloseForm}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingChannel ? "Edit Order Channel" : "Add Order Channel"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    name: e.target.value,
                                })
                            }
                            margin="normal"
                            required
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={formData.type}
                                label="Type"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        type: e.target.value as
                                            | "OFFLINE"
                                            | "ONLINE",
                                    })
                                }
                            >
                                <MenuItem value="OFFLINE">Offline</MenuItem>
                                <MenuItem value="ONLINE">Online</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_active: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Active"
                            sx={{ mt: 1 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseForm} disabled={formLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFormSubmit}
                        variant="contained"
                        disabled={formLoading}
                    >
                        {formLoading ? (
                            <CircularProgress size={24} />
                        ) : editingChannel ? (
                            "Update"
                        ) : (
                            "Create"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
